import React from "react";
import { Loader2, LucideIcon, RefreshCw } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { CtaButton } from "@/main/react/buttons/CtaButton";
import { PaginatedRenderPropsV2, PaginatedStatusSlot, PaginatedStatusSlots } from "@/main/react/list/PaginatedListWrapperV2";
import { ErrorBanner } from "@/main/react/component/ErrorBanner";
import { PaginatedListSsrFallback, SsrRestoredList } from "@/main/react/list/ssrFallbacks";

/**
 * The four reasons a list container holds no content, as four named renderings.
 *
 * <p>The canonical contract is "The state contract" in
 * `designs/04-live/2026-06-11-frontend-design-language-current-implementation.md` (rule 23), added
 * 2026-08-16 for ux-critique `EMPTY-STATES-45`. Read it before adding a fifth state or changing what
 * one of these looks like.
 *
 * <p><b>The invariant: a state is carried explicitly and is never inferred from the list.</b> A
 * component may not conclude it is empty because its array has length zero. `empty` is reachable only
 * from a successful response with nothing in it; `failed` keeps its own value rather than collapsing
 * into `null`; `filtered` is chosen by the HOST that owns the filter, because a shared container
 * cannot see a control it does not own.
 *
 * <p>Each rendering emits its name as `data-list-state`. That marker is what makes "these are four
 * different renderings" testable at all — and testing it as an INVARIANCE ACROSS SIBLINGS is the
 * point, because "the empty box is visible" was also true in the failure case, which was the whole
 * defect (`EMPTY-STATES-2`). `loading-more` is the incremental sibling of `loading` and is marked
 * separately so a spec can tell the first page's wait from the fifth page's.
 */
export type PaginatedListStateName = "loading" | "loading-more" | "empty" | "filtered" | "failed";

type EmptyStateAction = {
    label: React.ReactNode;
    onClick: () => void;
    icon?: LucideIcon;
};

function isEmptyStateAction(value: EmptyStateAction | React.ReactNode): value is EmptyStateAction {
    return typeof value === "object" && value != null && "label" in value && "onClick" in value;
}

export function PaginatedListEmptyState(props: {
    icon?: LucideIcon;
    title: React.ReactNode;
    description?: React.ReactNode;
    action?: EmptyStateAction | React.ReactNode;
    /**
     * A quiet line under the action — currently the contextual link into a product guide.
     *
     * Separate from `action` rather than folded into it because the group page's empty post list
     * already spends `action` on "Add the first post", and those two are wanted together, not
     * instead of each other. Keeping them apart also makes the guide link render identically across
     * every empty state whether or not that state has a CTA: a muted line, never a second button
     * competing with the primary one.
     */
    secondary?: React.ReactNode;
    /**
     * Which state this panel is standing for. Defaults to `empty`, so every existing call site is
     * unchanged.
     *
     * <p>Pass `"filtered"` only from a host that KNOWS one of its own controls emptied the list. The
     * container cannot work this out: it sees zero rows either way, and guessing is exactly how a
     * group with twenty posts and no questions came to be told "No posts yet" with a CTA aimed at the
     * wrong problem.
     *
     * <p>Pass `"failed"` when a surface has bespoke failure copy and wants this panel's shape rather
     * than {@link PaginatedListRetryState}'s banner. The panel recipe is shared on purpose — what
     * separates the states is the marker, the copy and the mandatory retry action, not the box. A
     * failure branch left marked `empty` is this component's own contract violated, which is what the
     * dashboard's Saved tab did until 2026-08-16.
     */
    variant?: "empty" | "filtered" | "failed";
    /**
     * Render the title as a real heading at this level instead of a `<div>`.
     *
     * <p>Optional, and omitting it preserves the previous render exactly — the same shape as
     * `CardTitle`'s `headingLevel`. The right level depends on the region the panel labels, which
     * only the page knows, so there is no safe default to impose on nineteen call sites.
     */
    titleHeadingLevel?: 2 | 3 | 4;
    className?: string;
}) {
    const { icon: Icon, title, description, action, secondary, variant, titleHeadingLevel, className } = props;
    const state: PaginatedListStateName = variant ?? "empty";
    const TitleTag = (titleHeadingLevel ? (`h${titleHeadingLevel}` as const) : "div");

    const renderedAction = React.useMemo(() => {
        if (!action) {
            return null;
        }

        if (!isEmptyStateAction(action)) {
            return action;
        }

        return (
            <CtaButton
                icon={action.icon ?? RefreshCw}
                text={action.label as string}
                size="sm"
                variant="primary"
                onClick={action.onClick}
            />
        );
    }, [action]);

    return (
        // `bg-muted` + a SOLID hairline, not the old `bg-background/60` + dashed.
        //
        // The old fill composited over the page's own `bg-background` to the same colour — measured
        // 1.00:1 of separation in both themes — so the only thing defining the panel was a dashed
        // border at 1.41:1 light / 1.95:1 dark, under the 3:1 WCAG 1.4.11 asks of a boundary you need
        // in order to identify a component, and roughly half a dashed perimeter is background anyway.
        // `bg-card` is only 1.04:1 against the page and does not fix it either. `bg-muted` is the
        // token that actually separates, and it is what the group rail's `EmptySectionState` already
        // used, so the product converges on one dialect instead of gaining a third. The dashed
        // outline is reserved for placeholders and drop targets, where it means something.
        //
        // `role="status"` because this is the TERMINAL state of a user-initiated change and the
        // transient loading slot below was already a live region — the announcement sequence was
        // inverted, so switching sub-tabs said "loading" and then nothing (`EMPTY-STATES-38`). Empty
        // and loading are mutually exclusive phases rendered into the same slot of
        // `PaginatedListWrapperV2`, so there is exactly one live region on screen at a time and a
        // load-more resolving to empty cannot double-announce.
        <div
            data-list-state={state}
            role="status"
            aria-live="polite"
            className={`w-full py-6 px-4 flex flex-col items-center text-center gap-3 rounded-xl border border-border bg-muted ${className ?? ""}`.trim()}
        >
            {/* `EMPTY-STATES-74`, operator decision **D6** (2026-08-26) — the decorative glyph does NOT
                wear the accent.

                This disc was `bg-primary/10 text-primary`, sampled `#0A55B8` light and `#619AE5` dark on
                the dashboard followers panel: byte-identical to the compose FAB's fill in the same
                frame. It is `aria-hidden`, it is not a control, and it cannot be
                clicked — while the panel's real primary button wears that same accent, and the guide
                link 16 CSS px below the button is muted-foreground with an `!` so no call site can
                promote it. Colour is the fastest affordance signal on a
                screen, and the loudest thing in the box was the one thing that does nothing.

                D6's answer is to mute the glyph rather than promote the link: the accent then belongs to
                the button, in every panel, whether or not that panel has one. `bg-background` rather than
                an alpha tint of the foreground — the panel is `bg-muted` precisely because it separates
                from the page's `bg-background`, so the same delta read the other way gives a real disc
                with no new token and no dilution (`EMPTY-STATES-75` is the cost of alpha suffixes).

                This lands on every empty / filtered / failed panel at once, including all ten `GuideLink`
                call sites, and that is what D6 chose — one decision about one primitive, not ten. */}
            {Icon ? (
                <div
                    data-playwright-id="paginated-list-empty-glyph"
                    className="rounded-full bg-background p-3 text-muted-foreground"
                >
                    <Icon className="size-5" aria-hidden="true" />
                </div>
            ) : null}
            <TitleTag className="font-medium">{title}</TitleTag>
            {description ? <div className="max-w-xl text-sm text-muted-foreground">{description}</div> : null}
            {renderedAction ? <div>{renderedAction}</div> : null}
            {secondary ? <div>{secondary}</div> : null}
        </div>
    );
}

/**
 * The default first-page wait: card-shaped skeleton rows.
 *
 * <p>Replaces a motionless centred line of text, which is the one thing a loading state must not be
 * confusable with — "there is nothing here" (`EMPTY-STATES-43`). A skeleton also reserves the height,
 * so the rows landing does not shift the page. Deliberately generic: this primitive does not know
 * what its consumer's rows look like, and a page that does should pass its own shaped skeleton as
 * `initialLoading` (`DiscussionCardListSkeleton` and friends).
 */
export function PaginatedListRowsSkeleton(props: { count?: number; className?: string }) {
    const { count = 3, className } = props;

    return (
        <div className={`flex min-w-0 w-full flex-col gap-3 ${className ?? ""}`.trim()} aria-hidden="true">
            {Array.from({ length: count }, (_, index) => (
                <div key={index} className="w-full overflow-hidden rounded-xl border border-border bg-card">
                    <div className="space-y-3 px-3 py-3 sm:px-4 sm:py-4">
                        <Skeleton className="h-5 w-2/3" />
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-5/6" />
                    </div>
                </div>
            ))}
        </div>
    );
}

/**
 * The `loading` marker and the live region that wrap whatever first-page placeholder a list uses.
 *
 * <p>Applied by {@link createStandardPaginatedStatusSlots} to the caller's `initialLoading` as well as
 * to the default skeleton, so a page that supplies its own shaped skeleton still emits the state
 * marker and still announces. A container that renders a non-content state without its marker cannot
 * be tested for the state-contract defect class at all, which is the condition every instance of it
 * has shipped in.
 */
export function PaginatedListInitialLoading(props: {
    label?: React.ReactNode;
    children: React.ReactNode;
}) {
    const { label, children } = props;

    return (
        <div className="w-full" data-list-state="loading" role="status" aria-live="polite">
            {label ? <span className="sr-only">{label}</span> : null}
            {children}
        </div>
    );
}

export function PaginatedListStatusText(props: {
    children: React.ReactNode;
    className?: string;
}) {
    const { children, className } = props;

    return (
        <div className={`w-full py-4 text-center text-muted-foreground ${className ?? ""}`.trim()}>
            {children}
        </div>
    );
}

export function PaginatedListLoadingMore(props: {
    label?: React.ReactNode;
    className?: string;
}) {
    const { label, className } = props;

    return (
        <div
            className={`w-full py-4 flex items-center justify-center gap-2 text-muted-foreground ${className ?? ""}`.trim()}
            role="status"
            aria-live="polite"
            data-list-state="loading-more"
            data-testid="paginated-list-loading-more"
        >
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            {label ? <span className="text-sm">{label}</span> : null}
        </div>
    );
}

export function PaginatedListRetryState(props: {
    message: React.ReactNode;
    retryLabel: React.ReactNode;
    onRetry: () => Promise<void>;
    className?: string;
}) {
    const { message, retryLabel, onRetry, className } = props;

    return (
        <div
            data-list-state="failed"
            className={`w-full py-4 flex flex-col items-center gap-3 text-center ${className ?? ""}`.trim()}
        >
            <ErrorBanner description={message} />
            <CtaButton
                icon={RefreshCw}
                text={retryLabel as string}
                size="sm"
                variant="outline"
                onClick={() => void onRetry()}
            />
        </div>
    );
}

/**
 * The initial-error slot: the retry affordance, plus the server's own list restored beneath it when
 * there is one.
 *
 * <p><b>Why this lives in the shared slot factory rather than per page.</b> Rule 32 says JS may only
 * improve on the HTML we shipped, and `getSsrSnapshotSubtreeHtml` had exactly two consumers repo-wide
 * while six islands mounted over a `crawlable_*_list` and rendered a retry card on failure. Putting
 * the restore here makes it one wiring line per page and gives a page added later the behaviour by
 * default — which is the difference between fixing six instances and closing the door.
 *
 * <p>Both are rendered, following `SubjectPage`'s precedent: the message tells a human what happened,
 * the restored list stops the page being emptier than the HTML already was.
 *
 * <p>Deliberately NOT wired into the `empty` slot. An empty successful response is a legitimate state,
 * and restoring a snapshot there would resurrect content the server has since stopped listing.
 */
function PaginatedListInitialErrorState(props: {
    ssrFallback?: PaginatedListSsrFallback;
    message: React.ReactNode;
    retryLabel: React.ReactNode;
    onRetry: () => Promise<void>;
}) {
    const { ssrFallback, message, retryLabel, onRetry } = props;

    return (
        <>
            <PaginatedListRetryState message={message} retryLabel={retryLabel} onRetry={onRetry} />
            <SsrRestoredList componentName={ssrFallback?.componentName} selector={ssrFallback?.selector} />
        </>
    );
}

export function createStandardPaginatedStatusSlots<T>(props: {
    initialLoading?: PaginatedStatusSlot<T>;
    loadingMessage: React.ReactNode;
    endMessage?: React.ReactNode;
    errorMessage: React.ReactNode;
    retryLabel: React.ReactNode;
    empty?: PaginatedStatusSlot<T>;
    /**
     * The rendering for "a control the viewer set emptied this list", used INSTEAD of `empty` while
     * {@link isFiltered} is true.
     *
     * <p>Two slots rather than one branch inside `empty` because the host, not the container, owns
     * the filter — see the note on `PaginatedListEmptyState.variant`. Omit both and nothing changes.
     */
    filtered?: PaginatedStatusSlot<T>;
    /** Whether one of the host's own filters is currently narrowing this list. */
    isFiltered?: boolean;
    loadingMore?: PaginatedStatusSlot<T>;
    end?: PaginatedStatusSlot<T>;
    /**
     * Suppress the end-of-list terminator below this many visible rows.
     *
     * <p>Omitted, the terminator renders exactly as before. Set it to the surface's page size and a
     * list that fitted in its first page stops printing "End of list" under a single card — pagination
     * machinery that rule 12 keeps out of user-facing copy, and a terminator where nothing was
     * paginated can suggest the list was cut short. The group page's leaders list took the same
     * decision by hand in 2026-07; this is that decision made available to every list
     * (`DASHBOARD-32`).
     */
    endMinItems?: number;
    initialError?: PaginatedStatusSlot<T>;
    loadMoreError?: PaginatedStatusSlot<T>;
    /** See {@link PaginatedListSsrFallback}. Omit for a list with no server-rendered counterpart. */
    ssrFallback?: PaginatedListSsrFallback;
}): PaginatedStatusSlots<T> {
    const {
        initialLoading,
        loadingMessage,
        endMessage,
        errorMessage,
        retryLabel,
        empty,
        filtered,
        isFiltered,
        loadingMore,
        end,
        endMinItems,
        initialError,
        loadMoreError,
        ssrFallback,
    } = props;

    return {
        initialLoading: (renderProps: PaginatedRenderPropsV2<T>) => (
            <PaginatedListInitialLoading label={loadingMessage}>
                {initialLoading == null
                    ? <PaginatedListRowsSkeleton />
                    : (typeof initialLoading === "function"
                        ? (initialLoading as (p: PaginatedRenderPropsV2<T>) => React.ReactNode)(renderProps)
                        : initialLoading)}
            </PaginatedListInitialLoading>
        ),
        loadingMore: loadingMore ?? <PaginatedListLoadingMore label={loadingMessage} />,
        empty: isFiltered && filtered != null ? filtered : empty,
        end: end ?? (endMessage
            ? (endMinItems == null
                ? <PaginatedListStatusText>{endMessage}</PaginatedListStatusText>
                : ({ visibleItemsCount }: PaginatedRenderPropsV2<T>) => (
                    visibleItemsCount < endMinItems
                        ? null
                        : <PaginatedListStatusText>{endMessage}</PaginatedListStatusText>
                ))
            : undefined),
        initialError: initialError ?? (({ onRetry }) => (
            <PaginatedListInitialErrorState
                ssrFallback={ssrFallback}
                message={errorMessage}
                retryLabel={retryLabel}
                onRetry={onRetry}
            />
        )),
        loadMoreError: loadMoreError ?? (({ onRetry }) => (
            <PaginatedListRetryState
                message={errorMessage}
                retryLabel={retryLabel}
                onRetry={onRetry}
            />
        )),
    };
}
